import React, { useState, useEffect, useMemo } from "react";
import LoadingSpinner from "../../../pages/dashboard/LoadingSpinner";

const DEFAULT_RADIO_OPTIONS = [
  { label: "All", value: "all" },
  { label: "New", value: "new" },
  { label: "Unchanged", value: "unchanged" },
  { label: "Modified", value: "modified" },
  { label: "Removed", value: "removed" },
];

const Table = ({
  data = [],
  title,
  columns = [],
  searchKey,

  enableRadioFilter = false,
  radioOptions = DEFAULT_RADIO_OPTIONS,
  radioField = "category", 
  radioDefault = "all",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [selectedRadio, setSelectedRadio] = useState(radioDefault);

  useEffect(() => {
    setSelectedRadio(radioDefault);
  }, [radioDefault]);

  useEffect(() => {
    if (data && data.length > 0) {
      setIsLoading(true);
      const timeout = setTimeout(() => setIsLoading(false), 500);
      return () => clearTimeout(timeout);
    }
    setIsLoading(false);
  }, [data]);

  const filteredData = useMemo(() => {
    let rows = Array.isArray(data) ? data : [];

    // 1) radio filter (only if enabled)
    if (enableRadioFilter && selectedRadio !== "all") {
      rows = rows.filter((row) => {
        const v = row?.[radioField];
        return String(v || "").toLowerCase() === selectedRadio;
      });
    }

    // 2) text search (existing)
    if (searchKey && searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((row) =>
        String(row?.[searchKey] || "").toLowerCase().includes(q)
      );
    }

    return rows;
  }, [data, enableRadioFilter, selectedRadio, radioField, searchKey, searchTerm]);

  return (
    <div className="card p-4 shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">{title}</h5>
        <small className="text-muted">{filteredData.length} rows</small>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "250px" }}>
          <LoadingSpinner title={`Loading ${title}...`} />
        </div>
      ) : (
        <>
          {/* radio filter (only when enabled) */}
          {enableRadioFilter && (
            <div className="d-flex flex-wrap gap-3 mb-3">
              {radioOptions.map((opt) => (
                <div className="form-check" key={opt.value}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`${title}-radio-filter`}
                    id={`${title}-radio-${opt.value}`}
                    value={opt.value}
                    checked={selectedRadio === opt.value}
                    onChange={(e) => setSelectedRadio(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor={`${title}-radio-${opt.value}`}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          )}

          {/* search */}
          {searchKey && (
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                placeholder={`Search by ${searchKey}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchTerm("")}
                disabled={!searchTerm}
              >
                Clear
              </button>
            </div>
          )}

          <div
            className="table-responsive"
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              maxHeight: "400px",
              overflowY: "auto",
              border: "1px solid #dee2e6",
              borderRadius: "0.375rem",
            }}
          >
            <table className="table table-hover table-striped align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  {columns.map((col, idx) => (
                    <th key={idx}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="text-center text-muted py-4">
                      No rows match the current filters.
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      {columns.map((col, idx) => (
                        <td key={idx}>{row[col.key]}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Table;
